import { CSSProperties, ReactNode } from "react"

import { Message } from "~/shared/ui/kit"
import { Contacts } from "~/shared/ui/components/Contacts"
import { Href } from "~/shared/ui/components/Href"
import { HSpace } from "~/shared/ui/components/HSpace"

import cssModule from "./index.module.css"
import { cx } from "classix"
import type { ContactsType } from "~/shared/ui/components/Contacts"

export type LinkProp = {
	/** определяет где открывается страница */
	target?: string
	/** контактные данные, которые отобразятся при наведении на ссылку */
	contact?: ContactsType
	url?: string
	/** выводимая вместо ссылки надпись */
	title: string | ReactNode
	/** подгруппа ссылок */
	sublinks?: LinkProp[]
	className?: string
	wrapperStyle?: CSSProperties
}

export type LinkSubgroupProp = {
	contact?: ContactsType
	links: LinkProp[]
}

export type LinkGroupProp = {
	title?: string
	subgroups: LinkSubgroupProp[]
}

export const emptyContact = {} as ContactsType

export function convertToLinkGroups(links: any[]) {
	const linkGroups = [] as LinkGroupProp[]

	for (const group of links) {
		const contactToLinksMap = new Map()

		if (group.sublinks) {
			for (const link of group.sublinks) {
				const key = link.contact ?? emptyContact

				if (!contactToLinksMap.has(key)) contactToLinksMap.set(key, [])

				const contactLinks = contactToLinksMap.get(key)

				contactLinks.push({
					title: link.title,
					url: link.url,
					sublinks: link.sublinks?.map((sublink) => ({
						title: sublink.title,
						url: sublink.url
					}))
				})
			}
		}

		linkGroups.push({
			title: group.title,
			subgroups: [...contactToLinksMap.entries()].map(
				([contact, links]) => ({
					contact,
					links
				})
			)
		})
	}

	return linkGroups
}

function Link({ link, index }: { link: LinkProp; index: number }) {
	return (
		<>
			<div className="flex items-start" style={link.wrapperStyle}>
				<span className="mr-[0.85rem] inline-block width-[1rem] leading-[1.5rem]">
					{index > 0 ? `${index}.` : "—"}
				</span>
				{link.url
					? <Href
						className={cx(cssModule.link, link.className)}
						href={link.url}
						target={link.target}
					>
						{link.title}
					</Href>
					: <div className={cx(cssModule.item, link.className)}>{link.title}</div>}
			</div>
			{link.sublinks && (
				<div className="flex flex-col gap-[0.75rem]">
					{link.sublinks.map((sublink, i) => (
						<div key={i} className="flex">
							<HSpace size={2} />
							<Link link={sublink} index={0} />
						</div>
					))}
				</div>
			)}
		</>
	)
}

function Subgroup({ subgroup, indexOffset = 0 }) {
	return (
		<Message
			type="info"
			style={{ marginBottom: "1rem", padding: "0" }}
		>
			<div className="flex flex-col gap-[0.75rem] p-[1rem]">
				{subgroup.links.map((link, i) => (
					<Link key={i} link={link} index={i + 1 + indexOffset} />
				))}
			</div>
			{subgroup.contact && subgroup.contact !== emptyContact && (
				<Contacts {...subgroup.contact} />
			)}
		</Message>
	)
}

function Group({ group }: { group: LinkGroupProp }) {
	let indexOffset = 0

	return (
		<div>
			{group.title && <h3 className="mb-[1rem] p-0">{group.title}</h3>}
			{group.subgroups.map((subgroup, i) => {
				indexOffset += i > 0 ? group.subgroups[i - 1].links.length : 0

				return (
					<Subgroup
						key={i}
						subgroup={subgroup}
						indexOffset={indexOffset}
					/>
				)
			})}
		</div>
	)
}

/** Выводит сгруппированные ссылки в отдельных блоках.
 * Для каждого блока можно указать заголовок и контакты (отобразятся снизу) */
export function LinkGroups({ groups }: { groups: LinkGroupProp[] }) {
	return (
		<>
			{groups.map((group, i) => (
				<Group key={i} group={group} />
			))}
		</>
	)
}
